<script setup>
import { storeToRefs } from 'pinia'
import Illustration from 'assets/illustrations/illu-04.svg?url'
import { computed } from 'vue'
import { useMessageStore } from '~/stores/messages'
import { useModalStore } from '~/stores/modal'

const props = defineProps({
    couponListConfig: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                placeholder: rawValue => rawValue?.placeholder === undefined || typeof rawValue?.placeholder === 'boolean',
                couponLink: rawValue => rawValue.couponLink === undefined || typeof rawValue.couponLink === 'string',
                shopUid: rawValue => rawValue.shopUid === undefined || typeof rawValue.shopUid === 'number',
                shopName: rawValue => rawValue.shopName === undefined || typeof rawValue.shopName === 'string'
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Get center details
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

onServerPrefetch(async () => {
    await centerDetailSuspense()
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Compute coupon teaser list class
const getCouponListClass = computed(() => {
    return {
        'coupon-teaser-list-container': !!props?.couponListConfig?.couponLink
    }
})

// Define app download config
const commonAppContent = computed(() => {
    return {
        title: staticKeys.value?.account_gettheapp_headline,
        description: staticKeys.value?.account_gettheapp_copytext
    }
})

// Modal store
const modalStore = useModalStore()
const { toggleModal, addModalContent } = modalStore

// Get tracking payload
const getTrackingPayload = (coupon) => {
    // Early return if tracking config or coupon is missing
    if (!props?.couponListConfig?.tracking || !coupon) return false

    // Destructure values for easier access
    const centerUid = centerDetails.value?.centerUid
    const centerName = centerDetails.value?.centerName
    const couponId = coupon?.uid
    const shopId = coupon?.shopIds?.length ? coupon.shopIds.join('|') : undefined
    const shopName = props?.couponListConfig?.shopName
    const couponTitle = coupon?.headline
    const couponValidity = getFormattedDate(coupon?.valid?.till)

    // Define the tracking object with reusable properties
    const trackingObj = {
        center: {
            centerUid,
            centerName,
            shopId,
            couponId,
            screenName: TRACKING_CONSTANTS.MISC.HOME_PAGE
        },
        shopDetail: {
            shopName,
            couponId,
            centerUid,
            couponTitle,
            couponValidity
        },
        centerCoupons: {
            centerUid,
            couponId,
            screenName: TRACKING_CONSTANTS.MISC.COUPONS_OVERVIEW
        }
    }

    return trackingObj[props.couponListConfig.tracking]
}
</script>

<template>
    <section v-if="couponListConfig?.items" class="coupons-list-container" :class="getCouponListClass">
        <div v-if="couponListConfig?.items?.length < 1" class="no-coupons-container">
            <p>
                <img :src="Illustration" :alt="staticKeys?.coupon_list_no_results" width="220" height="251">
                {{ staticKeys?.coupon_list_no_results }}
            </p>
        </div>

        <template v-else>
            <ul class="coupons-list">
                <CouponListItem
                    v-for="(coupon, index) in couponListConfig.items"
                    :key="coupon.uid"
                    :coupon="{
                        ...coupon,
                        index: index + 1,
                        placeholder: couponListConfig?.placeholder,
                        trackingPayload: getTrackingPayload(coupon),
                    }"
                    @open-modal="toggleModal(true); addModalContent(commonAppContent)"
                />
            </ul>

            <div v-if="couponListConfig.couponLink" class="coupons-list-overview-link-container">
                <NuxtLinkLocale
                    :to="couponListConfig.couponLink"
                    class="coupons-list-overview-link btn-active"
                >
                    {{ staticKeys?.coupon_teaser_listlink }}
                </NuxtLinkLocale>
            </div>
        </template>

        <!-- <DevOnly>
            <pre>{{ couponListConfig?.items }}</pre>
        </DevOnly> -->
    </section>
</template>

<style lang="scss">
.no-coupons-container,
.coupons-list-container {
    display: flex;
    flex-direction: column;
    margin-block-end: px2rem(60);

    @include bp(medium1) {
        margin-block-end: px2rem(68);
    }

    @include bp(large1) {
        margin-block-end: px2rem(90);
    }
}

.coupons-list-overview-link-container {
    display: none;
}

.coupons-list-overview-link {
    @include btn($type: 'primary');

}

.coupon-teaser-list-container {
    // More than 6 items
    &:has([data-coupon-item="7"]) {
        .coupons-list-overview-link-container {
            display: flex;

            @include bp(medium1) {
                display: none;
            }
        }
    }

    // More than 8 items
    &:has([data-coupon-item="9"]) {
        .coupons-list-overview-link-container {
            display: flex;

            @include bp(medium1) {
                display: flex;
            }

            @include bp(large1) {
                display: none;
            }
        }
    }

    // More than 9 items
    &:has([data-coupon-item="10"]) {
        .coupons-list-overview-link-container {
            display: flex;

            @include bp(medium1) {
                display: flex;
            }

            @include bp(large1) {
                display: flex;
            }
        }
    }

    [data-coupon-item] {
        display: none;

        @for $i from 1 through 6 {
            &[data-coupon-item="#{$i}"] {
                display: block;
            }
        }

        @for $i from 1 through 8 {
            &[data-coupon-item="#{$i}"] {
                @include bp(medium1) {
                    display: block;
                }
            }
        }

        @for $i from 1 through 9 {
            &[data-coupon-item="#{$i}"] {
                @include bp(large1) {
                    display: block;
                }
            }
        }
    }

}

.coupons-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, $element-max-width));
    justify-content: center;
    list-style-type: none;
    margin-block-end: px2rem(20);
    padding: 0;
    gap: $content-grid-mobile-gap;

    @include bp(medium1) {
        gap: $content-grid-tablet-gap;
    }

    @include bp(large1) {
        gap: $content-grid-desktop-gap;
    }
}

.no-coupons-container {
    text-align: center;
    max-width: 400px;
    margin: 0 auto;

    img {
        display: block;
        margin: 0 auto 40px;
    }
}
</style>
